import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const PICKER_SELECTOR = '[data-landmark-map-picker]';

const landmarkIcon = L.divIcon({
    className: '',
    html: `
        <div
            style="
                display:flex;
                align-items:center;
                justify-content:center;
                width:40px;
                height:40px;
                border-radius:9999px;
                border:3px solid white;
                background:#0891b2;
                color:white;
                font-size:19px;
                box-shadow:0 8px 22px rgba(0,0,0,.45);
            "
            aria-hidden="true"
        >
            📍
        </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -42],
});

function parseCoordinate(value) {
    const parsed = Number.parseFloat(value);

    return Number.isFinite(parsed)
        ? parsed
        : null;
}

function initializeLandmarkMapPicker(picker) {
    if (picker.dataset.landmarkMapReady === 'true') {
        return;
    }

    const form = picker.closest('form');
    const mapElement = picker.querySelector(
        '[data-landmark-map]'
    );
    const status = picker.querySelector(
        '[data-landmark-map-status]'
    );
    const latitudeInput = form?.querySelector(
        '[data-landmark-latitude]'
    );
    const longitudeInput = form?.querySelector(
        '[data-landmark-longitude]'
    );

    if (
        !form
        || !mapElement
        || !latitudeInput
        || !longitudeInput
    ) {
        return;
    }

    picker.dataset.landmarkMapReady = 'true';

    const savedLatitude = parseCoordinate(
        latitudeInput.value
    );
    const savedLongitude = parseCoordinate(
        longitudeInput.value
    );
    const centerLatitude = parseCoordinate(
        picker.dataset.centerLatitude
    );
    const centerLongitude = parseCoordinate(
        picker.dataset.centerLongitude
    );

    const initialLatitude =
        savedLatitude ?? centerLatitude ?? 0;
    const initialLongitude =
        savedLongitude ?? centerLongitude ?? 0;
    const hasUsefulCenter =
        savedLatitude !== null
        || centerLatitude !== null;

    const map = L.map(mapElement, {
        zoomControl: true,
        scrollWheelZoom: false,
    }).setView(
        [initialLatitude, initialLongitude],
        hasUsefulCenter ? 17 : 2
    );

    L.tileLayer(
        'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        {
            maxZoom: 20,
            attribution:
                '&copy; OpenStreetMap contributors',
        }
    ).addTo(map);

    let marker = null;
    let syncingInputs = false;

    const setStatus = (latitude, longitude) => {
        if (!status) {
            return;
        }

        status.textContent =
            `Selected point: ${latitude.toFixed(7)}, ${longitude.toFixed(7)}`;
    };

    const syncInputs = (
        latitude,
        longitude
    ) => {
        syncingInputs = true;

        latitudeInput.value =
            latitude.toFixed(7);
        longitudeInput.value =
            longitude.toFixed(7);

        syncingInputs = false;
        setStatus(latitude, longitude);
    };

    const placeMarker = (
        latitude,
        longitude,
        center = false
    ) => {
        const point = L.latLng(
            latitude,
            longitude
        );

        if (!marker) {
            marker = L.marker(
                point,
                {
                    draggable: true,
                    icon: landmarkIcon,
                }
            ).addTo(map);

            marker.on(
                'dragend',
                () => {
                    const position =
                        marker.getLatLng();

                    syncInputs(
                        position.lat,
                        position.lng
                    );
                }
            );
        } else {
            marker.setLatLng(point);
        }

        if (center) {
            map.setView(
                point,
                Math.max(map.getZoom(), 17),
                {
                    animate: true,
                }
            );
        }

        setStatus(
            latitude,
            longitude
        );
    };

    map.on(
        'click',
        (event) => {
            placeMarker(
                event.latlng.lat,
                event.latlng.lng
            );

            syncInputs(
                event.latlng.lat,
                event.latlng.lng
            );
        }
    );

    const syncFromInputs = () => {
        if (syncingInputs) {
            return;
        }

        const latitude = parseCoordinate(
            latitudeInput.value
        );
        const longitude = parseCoordinate(
            longitudeInput.value
        );

        if (
            latitude === null
            || longitude === null
        ) {
            return;
        }

        placeMarker(
            latitude,
            longitude,
            true
        );
    };

    latitudeInput.addEventListener(
        'input',
        syncFromInputs
    );
    longitudeInput.addEventListener(
        'input',
        syncFromInputs
    );

    if (
        savedLatitude !== null
        && savedLongitude !== null
    ) {
        placeMarker(
            savedLatitude,
            savedLongitude,
            true
        );
    }

    window.setTimeout(
        () => map.invalidateSize(),
        100
    );
}

function initializeLandmarkMapPickers() {
    document
        .querySelectorAll(PICKER_SELECTOR)
        .forEach(initializeLandmarkMapPicker);
}

if (document.readyState === 'loading') {
    document.addEventListener(
        'DOMContentLoaded',
        initializeLandmarkMapPickers,
        {
            once: true,
        }
    );
} else {
    initializeLandmarkMapPickers();
}

document.addEventListener(
    'livewire:navigated',
    initializeLandmarkMapPickers
);
